import { QualityControl } from '../../domain/entities/quality-control.entity';
import { QcAsset } from '../../domain/entities/quality-control-asset.entity';
import { AssetUseType } from '../../domain/entities/asset-use-type.entity';
import { AssetRequiredField } from '../../domain/entities/asset-required-field.entity';
import { KoOption } from '../../domain/entities/ko-option.entity';
import { QualityControlOriginOption } from '../../domain/entities/quality-control-origin.entity';
import { QualityControlInterfaceData } from '../../domain/entities/quality-control-interface-data.entity';
import { QualityControlFinishResult, QualityControlIssue } from '../../domain/entities/quality-control-finish-result.entity';
import { EditAssetItemDto } from '../../application/dtos/edit-assets.dto';
import { QualityControlStatus } from '../../domain/value-objects/quality-control-status.vo';
import { QualityControlIssueType } from '../../domain/value-objects/quality-control-issue-type.vo';
import { ContractId } from '../../../orders/domain/value-objects/contract-id.vo';
import {
  QcVerifications,
  QcAppraisalVerification,
  QcAssetVerification,
  QcVerification,
} from '../../domain/entities/quality-control-verification.entity';
import { RevisionResult } from '../../domain/value-objects/revision-result.vo';
import { PropertyRegisterVerificationResult } from '../../domain/value-objects/property-register-verification-result.vo';

export interface QualityControlListItemApiResponse {
  id: string;
  version: number;
  createdAt: string;
  statusValue: number;
  appraisalName: string | null;
  technicalUserIds: string | null;
  orderId: string | null;
  code: string | null;
  finishedAt: string | null;
  qualityControlResult: number | null;
  isCancelled?: boolean;
  userName?: string | null;
  contractId?: number | null;
  origin?: number | null;
}

export interface QualityControlDetailApiResponse {
  id: string;
  version?: number;
  createdAt?: string;
  statusValue?: number;
  appraisalName?: string | null;
  orderId?: string | null;
  appraisalCompany: string | null;
  appraisalPurpose: string | null;
  finishedAt: string | null;
  qualityControlResult: number | null;
  assignedTechnicalUserFullName?: string | null;
  contractId?: number | null;
  appraisalReportNumber?: string | null;
  appraisalIssueDate?: string | null;
  appraiserName?: string | null;
  origin?: number | null;
}

export interface QualityControlListPagedApiResponse {
  items: QualityControlListItemApiResponse[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
}

// ─── Validations API types ────────────────────────────────────────────────────

export interface QcVerificationApiResponse {
  id: string;
  result: number;
  koResult: number | null;
  observations: string | null;
}

export interface QcAppraisalValidationApiResponse {
  totalValue: number | null;
  estimatedValue: number | null;
  deviation: number | null;
  qualityControlResult: number | null;
  verifications: QcVerificationApiResponse[] | null;
}

export interface QcAssetValidationApiResponse {
  id: string;
  name: string | null;
  propertyRegistryNumber: string | null;
  totalValue: number | null;
  estimatedValue: number | null;
  deviation: number | null;
  revisionResult: number | null;
  verifications: QcVerificationApiResponse[] | null;
}

export interface QcValidationsApiResponse {
  qualityControlVerifications: QcAppraisalValidationApiResponse | null;
  assetsVerifications: QcAssetValidationApiResponse[] | null;
}

// ─── Assets API types ─────────────────────────────────────────────────────────

export interface QcAssetApiResponse {
  id: string;
  assetName: string | null;
  propertyRegistryNumber: string | null;
  propertyRegistry: string | null;
  assetUse: number | null;
  ineCodProv: number | null;
  ineCodMun: number | null;
  cadastralReferences: string[] | null;
  idufir: string | null;
  constructionStage: number | null;
  visitType: number | null;
  publicProtectionType: number | null;
  adoptedValorationMethod: number | null;
  adoptedSurfaceValue: number | null;
  adoptedSurface: number | null;
  unitValue: number | null;
  totalValue: number | null;
}

export interface QcAssetsApiResponse {
  items: QcAssetApiResponse[] | null;
}

// ─── AssetUseTypes API types ──────────────────────────────────────────────────

export interface AssetUseTypeApiResponse {
  value: number;
  name: string | null;
  displayName: string | null;
  contractId: number | null;
}

// ─── AssetRequiredFields API types ───────────────────────────────────────────

export interface AssetRequiredFieldApiResponse {
  name: string | null;
  contractIds: number[] | null;
}

// ─── KoOptions API types ──────────────────────────────────────────────────────
// TODO: actualizar cuando back confirme el modelo real del endpoint

export interface KoOptionApiResponse {
  value: number;
  name: string | null;
  displayName: string | null;
}

// ─── QualityControlOrigin API types ──────────────────────────────────────────

export interface QualityControlOriginApiResponse {
  value: number;
  name: string | null;
  displayName: string | null;
}

// ─── FinishResult API types ───────────────────────────────────────────────────

export interface QcIssueApiResponse {
  id: string;
  issueRuleCode: number;
  issueRuleName: string | null;
  text: string | null;
  fields: string[] | null;
  issueType: number;
  issueRuleLevelName: string | null;
  createdAt: string;
}

export interface QcAssetIssueApiResponse {
  assetId: string;
  assetName: string | null;
  propertyRegistryNumber: string | null;
  issues: QcIssueApiResponse[];
}

export interface QcFinishResultApiResponse {
  finished: boolean;
  qualityControlIssues: QcIssueApiResponse[];
  assetIssues: QcAssetIssueApiResponse[];
}

export class QualityControlMapper {
  static toDomain(raw: QualityControlDetailApiResponse): QualityControl {
    return {
      id: raw.id,
      createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(0),
      version: raw.version ?? 0,
      statusValue: raw.statusValue != null ? raw.statusValue as QualityControlStatus : QualityControlStatus.PendingAssignment,
      appraisalName: raw.appraisalName ?? null,
      technicalUserIds: raw.assignedTechnicalUserFullName ?? null,
      assignedTechnicalUserFullName: raw.assignedTechnicalUserFullName ?? null,
      orderId: raw.orderId ?? null,
      code: null,
      appraisalCompany: raw.appraisalCompany,
      appraisalPurpose: raw.appraisalPurpose,
      contractId: raw.contractId != null ? raw.contractId as ContractId : null,
      finishedAt: raw.finishedAt ? new Date(raw.finishedAt) : null,
      qualityControlResult: raw.qualityControlResult as RevisionResult | null,
      appraisalReportNumber: raw.appraisalReportNumber ?? null,
      appraisalIssueDate: raw.appraisalIssueDate ? new Date(raw.appraisalIssueDate) : null,
      appraiserName: raw.appraiserName ?? null,
      origin: raw.origin ?? null,
    };
  }

  static toDomainFromList(raw: QualityControlListItemApiResponse): QualityControl {
    return {
      id: raw.id,
      createdAt: new Date(raw.createdAt),
      version: raw.version,
      statusValue: raw.statusValue as QualityControlStatus,
      appraisalName: raw.appraisalName,
      technicalUserIds: raw.technicalUserIds,
      assignedTechnicalUserFullName: raw.userName ?? null,
      orderId: raw.orderId,
      code: raw.code ?? null,
      appraisalCompany: null,
      appraisalPurpose: null,
      contractId: raw.contractId != null ? raw.contractId as ContractId : null,
      finishedAt: raw.finishedAt ? new Date(raw.finishedAt) : null,
      qualityControlResult: raw.qualityControlResult as RevisionResult | null,
      isCancelled: raw.isCancelled ?? false,
      origin: raw.origin ?? null,
    };
  }

  static toDomainList(rawList: QualityControlListItemApiResponse[]): QualityControl[] {
    return rawList.map((raw) => this.toDomainFromList(raw));
  }

  // ─── Validations ────────────────────────────────────────────────────────────

  private static mapVerification(raw: QcVerificationApiResponse): QcVerification {
    return {
      id: raw.id,
      result: raw.result as PropertyRegisterVerificationResult,
      koResult: raw.koResult,
      observations: raw.observations,
    };
  }

  private static mapAppraisalValidation(raw: QcAppraisalValidationApiResponse | null): QcAppraisalVerification {
    if (!raw) {
      return { totalValue: null, estimatedValue: null, deviation: null, qualityControlResult: null, verifications: [] };
    }
    return {
      totalValue: raw.totalValue,
      estimatedValue: raw.estimatedValue,
      deviation: raw.deviation,
      qualityControlResult: raw.qualityControlResult as RevisionResult | null,
      verifications: (raw.verifications ?? []).map((v) => this.mapVerification(v)),
    };
  }

  private static mapAssetValidation(raw: QcAssetValidationApiResponse): QcAssetVerification {
    return {
      id: raw.id,
      name: raw.name,
      propertyRegistryNumber: raw.propertyRegistryNumber,
      totalValue: raw.totalValue,
      estimatedValue: raw.estimatedValue,
      deviation: raw.deviation,
      revisionResult: raw.revisionResult as RevisionResult | null,
      verifications: (raw.verifications ?? []).map((v) => this.mapVerification(v)),
    };
  }

  static toValidationsDomain(raw: QcValidationsApiResponse): QcVerifications {
    return {
      qualityControlVerifications: this.mapAppraisalValidation(raw.qualityControlVerifications),
      assetsVerifications: (raw.assetsVerifications ?? []).map((av) => this.mapAssetValidation(av)),
    };
  }

  // ─── Assets ──────────────────────────────────────────────────────────────────

  static toAssetsDomain(raw: QcAssetsApiResponse): QcAsset[] {
    return (raw.items ?? []).map((item) => ({
      id: item.id,
      assetName: item.assetName,
      propertyRegistryNumber: item.propertyRegistryNumber,
      propertyRegistry: item.propertyRegistry,
      assetUse: item.assetUse,
      ineCodProv: item.ineCodProv,
      ineCodMun: item.ineCodMun,
      cadastralReferences: item.cadastralReferences,
      idufir: item.idufir,
      constructionStage: item.constructionStage,
      visitType: item.visitType,
      publicProtectionType: item.publicProtectionType,
      adoptedValorationMethod: item.adoptedValorationMethod,
      adoptedSurfaceValue: item.adoptedSurfaceValue,
      adoptedSurface: item.adoptedSurface,
      unitValue: item.unitValue,
      totalValue: item.totalValue,
    }));
  }

  static toAssetTableRows(items: QcAsset[]): Record<string, string>[] {
    return items.map((asset) => ({
      assetName: asset.assetName ?? '',
      propertyRegistryNumber: asset.propertyRegistryNumber ?? '',
      propertyRegistryEnumValue: asset.propertyRegistry ?? '',
      assetUse: asset.assetUse != null ? String(asset.assetUse) : '',
      ineCodProv: asset.ineCodProv != null ? String(asset.ineCodProv) : '',
      ineCodMun: asset.ineCodMun != null ? String(asset.ineCodMun) : '',
      cadastralReferences: asset.cadastralReferences?.join(', ') ?? '',
      idufir: asset.idufir ?? '',
      constructionStage: asset.constructionStage != null ? String(asset.constructionStage) : '',
      visitType: asset.visitType != null ? String(asset.visitType) : '',
      publicProtectionType: asset.publicProtectionType != null ? String(asset.publicProtectionType) : '',
      adoptedValorationMethod: asset.adoptedValorationMethod != null ? String(asset.adoptedValorationMethod) : '',
      adoptedSurfaceValue: asset.adoptedSurfaceValue != null ? String(asset.adoptedSurfaceValue) : '',
      adoptedSurface: asset.adoptedSurface != null ? String(asset.adoptedSurface) : '',
      unitValue: asset.unitValue != null ? String(asset.unitValue) : '',
      totalValue: asset.totalValue != null ? String(asset.totalValue) : '',
    }));
  }

  static sortFieldToApi(field: string): string {
    const mapping: Record<string, string> = {
      orderId: 'orderid',
      code: 'code',
      version: 'version',
      appraisalName: 'appraisalname',
      createdAt: 'createdat',
      statusValue: 'status',
      technicalUserIds: 'technicalUserIds',
      finishedAt: 'finishedat',
      qualityControlResult: 'qualitycontrolresult',
    };
    return mapping[field] ?? field.toLowerCase();
  }

  static dateFilterToIso(value: unknown): string {
    const date = value instanceof Date ? value : new Date(value as string);
    return date.toISOString();
  }

  static toAssetUseTypesDomain(items: AssetUseTypeApiResponse[]): AssetUseType[] {
    return items.map(item => ({
      value: item.value,
      name: item.name,
      displayName: item.displayName,
      contractId: item.contractId,
    }));
  }

  static toAssetRequiredFieldsDomain(items: AssetRequiredFieldApiResponse[]): AssetRequiredField[] {
    return items.map(item => ({
      name: item.name,
      contractIds: item.contractIds,
    }));
  }

  static toKoOptionsDomain(items: KoOptionApiResponse[]): KoOption[] {
    return items.map(item => ({
      value: item.value,
      name: item.name,
      displayName: item.displayName,
    }));
  }

  static toQualityControlOriginsDomain(items: QualityControlOriginApiResponse[]): QualityControlOriginOption[] {
    return items.map(item => ({
      value: item.value,
      name: item.name,
      displayName: item.displayName,
    }));
  }

  static toEditAssetItemDtos(
    rows: Record<string, string>[],
    ids: string[],
  ): EditAssetItemDto[] {
    return rows.map((row, i) => ({
      id: ids[i] || undefined,
      assetName: row['assetName'] || undefined,
      propertyRegistryNumber: row['propertyRegistryNumber'] || undefined,
      propertyRegistryEnumValue: row['propertyRegistryEnumValue'] || undefined,
      assetUse: row['assetUse'] ? Number(row['assetUse']) : undefined,
      ineCodProv: row['ineCodProv'] ? Number(row['ineCodProv']) : undefined,
      ineCodMun: row['ineCodMun'] ? Number(row['ineCodMun']) : undefined,
      cadastralReferences: row['cadastralReferences'] ? [row['cadastralReferences']] : undefined,
      idufir: row['idufir'] || undefined,
      constructionStage: row['constructionStage'] ? Number(row['constructionStage']) : undefined,
      visitType: row['visitType'] ? Number(row['visitType']) : undefined,
      publicProtectionType: row['publicProtectionType'] !== undefined && row['publicProtectionType'] !== '' ? Number(row['publicProtectionType']) : undefined,
      adoptedValorationMethod: row['adoptedValorationMethod'] ? Number(row['adoptedValorationMethod']) : undefined,
      adoptedSurfaceValue: row['adoptedSurfaceValue'] ? Number(row['adoptedSurfaceValue']) : undefined,
      adoptedSurface: row['adoptedSurface'] ? Number(row['adoptedSurface']) : undefined,
      unitValue: row['unitValue'] ? Number(row['unitValue']) : undefined,
      totalValue: row['totalValue'] ? Number(row['totalValue']) : undefined,
    }));
  }

  private static mapIssue(raw: QcIssueApiResponse): QualityControlIssue {
    return {
      id: raw.id,
      issueRuleCode: raw.issueRuleCode,
      issueRuleName: raw.issueRuleName,
      text: raw.text,
      fields: raw.fields ?? null,
      issueType: raw.issueType as QualityControlIssueType,
      issueRuleLevelName: raw.issueRuleLevelName,
      createdAt: new Date(raw.createdAt),
    };
  }

  static toFinishResultDomain(raw: QcFinishResultApiResponse): QualityControlFinishResult {
    return {
      finished: raw.finished,
      qualityControlIssues: (raw.qualityControlIssues ?? []).map((i) => this.mapIssue(i)),
      assetIssues: (raw.assetIssues ?? []).map((a) => ({
        assetId: a.assetId,
        assetName: a.assetName,
        propertyRegistryNumber: a.propertyRegistryNumber,
        issues: (a.issues ?? []).map((i) => this.mapIssue(i)),
      })),
    };
  }

  static toInterfaceDataDomain(raw: QualityControlInterfaceDataApiResponse): QualityControlInterfaceData {
    return {
      qualityControlId: raw.qualityControlId,
      contractId: raw.contractId ?? null,
      interfaceData: raw.interfaceData ?? null,
      assets: (raw.assets ?? []).map((a) => ({
        assetId: a.assetId,
        assetName: a.assetName ?? null,
        propertyRegistryNumber: a.propertyRegistryNumber ?? null,
        interfaceData: a.interfaceData ?? null,
      })),
    };
  }
}

// ─── InterfaceData API types ──────────────────────────────────────────────────

export type QcInterfaceDataApiResponse = Record<string, string | null>;

export type QcAssetInterfaceDataApiResponse = Record<string, string | null>;

export interface QcAssetInterfaceDataItemApiResponse {
  assetId: string;
  assetName: string | null;
  propertyRegistryNumber: string | null;
  interfaceData: QcAssetInterfaceDataApiResponse | null;
}

export interface QualityControlInterfaceDataApiResponse {
  qualityControlId: string;
  contractId: number | null;
  interfaceData: QcInterfaceDataApiResponse | null;
  assets: QcAssetInterfaceDataItemApiResponse[] | null;
}
